<script>
	import { navigate } from "svelte-routing";
	import { Col, Row } from "sveltestrap";
	import {
		bookingId,
		damageReviewed,
		errorMessage,
		email,
	} from "src/store/store";
	import { onMount } from "svelte";

	import ConfirmModal from "../../../../components/CustomModal/ConfirmModal.svelte";
	import CustomButton from "../../../../components/CustomButton/CustomButton.svelte";
	import CustomCollapseCard from "../../../../components/CustomCollapseCard/CustomCollapseCard.svelte";
	import ErrorMessageHandler from "../../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";

	import PageHeader from "../../../../components/PageHeader/PageHeader.svelte";
	import {
		extractErrorMessage,
		scrollToTop,
		splitCamelCaseAndCapitalize,
	} from "src/utils/misc";
	import client from "src/services/client";
	import { convert } from "src/services/helpers";

	//#region Variables
	let id = null;
	let vehicleId = null;

	let err;
	let cases = [];
	let idxMap = {};

	const searchParams = new URLSearchParams(location.search);
	//#endregion

	errorMessage.subscribe((value) => {
		err = value;
	});

	//#region API Calls
	//WRITE API CALLS HERE
	//#endregion

	/**
	 * Loads cases from localStorage for the current vehicleId.
	 * Populates the `cases` array and initializes `idxMap` for image navigation.
	 */
	function loadCasesFromStorage() {
		if (!vehicleId) return;

		const key = `damageReviewedData`;
		const stored = JSON.parse(localStorage.getItem(key) || "[]");

		stored.shift(); // Remove the first element if it exists

		cases = stored.map((item) => ({
			...item,
			images: (item.images || []).slice(1),
		}));

		let unknownCases = [];

		cases.forEach((caseData) => {
			const unknownIndex = caseData.images.findIndex((image) => {
				return image.damageAttribute === "unknown";
			});
			if (unknownIndex !== -1) {
				unknownCases.push({
					...caseData,
					reportType: caseData.reportType,
					bookingNo: "Unknown",
					otherBookingNo: caseData.bookingNo,
					images: caseData.images.map((img) => ({
						...img,
						damageAttribute:
							img.damageAttribute !== "unknown"
								? "none"
								: "unknown",
					})),
				});
				caseData.images[unknownIndex].damageAttribute = "none";
			}
		});
		cases = [...cases, ...unknownCases];

		// loop through cases, if all images in a case have all damageAttribute as none, remove the object from cases
		cases = cases.filter((caseData) => {
			return caseData.images.some((image) => {
				return image.damageAttribute !== "none";
			});
		});

		idxMap = {};
		cases.forEach((_, i) => {
			idxMap[i] = 0;
		});

		cases.forEach(async (caseData, index) => {
			if (caseData.bookingNo !== "Unknown") {
				const bookingData = await fetchBookingStartEndDateTime(
					caseData.bookingNo
				);
				if (bookingData) {
					cases[index].startTime = bookingData.startTime;
					cases[index].endTime = bookingData.endTime;
				}
			}
		});
	}

	async function fetchBookingStartEndDateTime(bookingNo) {
		try {
			const res = await client(
				`/v1/CaseManagement/booking/${bookingNo}`,
				{
					method: "GET",
					version: "Tasking",
				}
			);
			if (res.status === 200) {
				const bookingData = res.data;
				return {
					startTime: bookingData.startDateTime,
					endTime: bookingData.endDateTime,
				};
			}
		} catch (error) {
			console.error("Error fetching booking start and end date:", error);
			extractErrorMessage(error);
		}
	}

	async function handleSubmit() {
		// loop in cases and check filter the images, if image has damageAttribute = 'none', remove the object
		let caseReq = cases.map((caseData) => {
			caseData.images = caseData.images.filter(
				(image) => image.damageAttribute !== "none"
			);
			return caseData;
		});

		let data = caseReq.map((caseData) => {
			return {
				vehicleId: caseData.vehicleId,
				bookingNo:
					caseData.bookingNo === "Unknown"
						? caseData.otherBookingNo
						: caseData.bookingNo,
				bookingId: caseData.bookingId,
				attribution:
					caseData.images[0].damageAttribute === "bid"
						? true
						: caseData.images[0].damageAttribute === "unknown"
						? false
						: null,
				bookingReportType: caseData.reportType,
				investigator: $email,
				highlightedDamagedSides: caseData.images.map((image) => {
					return {
						uid: image.uid,
						side: image.imgSide,
						url: image.imgUrl,
					};
				}),
			};
		});

		try {
			const res = await client("/v1/CaseManagement/case", {
				method: "POST",
				version: "Tasking",
				data: {
					cases: data,
				},
			});
			if (res.status === 204) {
				navigate("/issues/case-management?pageSize=20&tab=2");
				localStorage.removeItem("damageReviewedData");
			}
		} catch (error) {
			console.log(error);
			extractErrorMessage(error);
			scrollToTop();
		}
	}

	onMount(async () => {
		id = searchParams.get("id");
		vehicleId = searchParams.get("vehicleId");
		loadCasesFromStorage();
	});
</script>

<div>
	<Row class="max-height">
		<Col xs={12}>
			<div>
				<ErrorMessageHandler message={err} />
				<Col style="margin-bottom: 24px;">
					<PageHeader title="Submit Investigation" />
				</Col>

				<!-- #region Case -->
				{#each cases as caseData, index}
					<div class="mb-4">
						<CustomCollapseCard
							title={`Case ${index + 1}`}
							isOpenDefault={true}
						>
							<div class="p-0">
								<!-- Metadata -->
								<div class="row mb-3">
									<div class="col">
										<div class="description">
											Booking ID & Report Type
										</div>
										<div class="value">
											{caseData.bookingNo} ({splitCamelCaseAndCapitalize(
												caseData.reportType
											)})
										</div>
									</div>
									<div class="col">
										<div class="description">
											Start Date & Time
										</div>
										<div class="value">
											{convert(caseData.startTime) ||
												"N/A"}
										</div>
									</div>
									<div class="col">
										<div class="description">
											End Date & Time
										</div>
										<div class="value">
											{convert(caseData.endTime) || "N/A"}
										</div>
									</div>
								</div>
								<hr class="divider" />
								<!-- Image gallery -->
								<div
									class="d-flex justify-content-center align-items-center my-5 flex-column"
								>
									<div
										class="d-flex justify-content-between w-25 my-2 damage-image-container"
									>
										<img
											src="/assets/icon-chevron-left-thin.svg"
											alt="left-arrow"
											class="arrow"
											as="button"
											on:click={() => {
												if ((idxMap[index] ?? 0) > 0) {
													idxMap[index] =
														(idxMap[index] ?? 0) -
														1;
												}
											}}
										/>

										<div class="image-wrapper">
											{#if caseData.images.length > 0}
												<img
													src={caseData.images[
														idxMap[index]
													].imgUrl}
													alt={caseData.images[
														idxMap[index]
													].imgSide}
													class="damage-image"
													style="border: {(caseData.bookingNo ===
														'Unknown' &&
														caseData.images[
															idxMap[index]
														].damageAttribute ===
															'unknown') ||
													(caseData.bookingNo !==
														'Unknown' &&
														caseData.images[
															idxMap[index]
														].damageAttribute ===
															'bid')
														? '2px solid red'
														: 'none'};"
												/>

												{#if (caseData.bookingNo === "Unknown" && caseData.images[idxMap[index]].damageAttribute === "unknown") || (caseData.bookingNo !== "Unknown" && caseData.images[idxMap[index]].damageAttribute === "bid")}
													<img
														src="/assets/icon-alert-filled-red.svg"
														alt="alert"
														class="alert-icon-thumb"
													/>
												{/if}
											{/if}
										</div>

										<img
											src="/assets/icon-chevron-right-thin.svg"
											alt="right-arrow"
											class="arrow"
											as="button"
											on:click={() => {
												if (
													(idxMap[index] ?? 0) <
													caseData.images.length - 1
												) {
													idxMap[index] =
														(idxMap[index] ?? 0) +
														1;
												}
											}}
										/>
									</div>

									<!-- Thumbnails -->
									<div
										class="d-flex align-items-center justify-content-center mt-3"
									>
										<div class="thumbnail-wrapper">
											{#each caseData.images as image, i}
												<div
													class="thumbnail-container"
												>
													<img
														src={image.imgUrl}
														alt={image.imgSide}
														class="damages-image"
														on:click={() => {
															idxMap[index] = i;
														}}
														style="border: {idxMap[
															index
														] === i
															? '2px solid blue'
															: (caseData.bookingNo ===
																	'Unknown' &&
																	image.damageAttribute ===
																		'unknown') ||
															  (caseData.bookingNo !==
																	'Unknown' &&
																	image.damageAttribute ===
																		'bid')
															? '2px solid red'
															: 'none'};"
													/>
													<!-- If bookingNo and image.damageAttribute -->

													{#if (caseData.bookingNo === "Unknown" && image.damageAttribute === "unknown") || (caseData.bookingNo !== "Unknown" && image.damageAttribute === "bid")}
														<img
															src="/assets/icon-alert-filled-red.svg"
															alt="alert"
															class="alert-icon-thumb"
														/>
													{/if}
												</div>
											{/each}
										</div>
									</div>
								</div>
							</div>
						</CustomCollapseCard>
					</div>
				{/each}

				<!-- #endregion -->
			</div>
		</Col>
	</Row>

	<!-- #region Action Buttons -->
	<!-- Back button -->
	<div class="d-flex flex-row justify-content-end my-1">
		<div class="mx-1">
			<CustomButton
				buttonText="Back"
				onClick={() =>
					navigate(`/issues/case-management/${id}`, {
						replace: true,
					})}
			/>
		</div>

		<!-- Submit button -->
		<div class="mx-1">
			<CustomButton
				buttonText="Submit Investigation"
				color="custom-blue"
				onClick={handleSubmit}
			/>
		</div>
	</div>
	<!-- #endregion -->
</div>

<style>
	:global(.max-height) {
		height: fit-content;
	}

	:global(.divider) {
		border: 0.5px solid var(--fds-on-surface-variant);
		margin: 24px 0;
	}

	.description {
		color: var(--fds-on-surface-variant);
	}

	.report-container {
		margin-bottom: 24px;
	}

	.last-reviewed-badge {
		position: absolute;
		top: -16px;
		left: 0;
		background-color: #248a3d; /* green */
		color: white;
		font-weight: 700;
		font-size: 16px;
		padding: 5px 18px;
		border-radius: 4px;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		z-index: 1;
	}

	.image-wrapper {
		position: relative;
		display: inline-block;
	}
	.description {
		color: var(--fds-on-surface-variant);
	}

	.review-container {
		border: 1px solid #e8e8e8;
		border-radius: 8px;
	}

	.info-category {
		border-bottom: 1px solid #e8e8e8;
	}

	.damage-image-container {
		height: 90%;
	}

	.damage-image {
		height: -webkit-fill-available;
		width: 100%;
		object-fit: cover;
		display: block;
		max-width: 100%;
	}

	.damages-image {
		height: 40px;
		width: 40px;
		margin: 0 2px;
	}

	img:hover {
		cursor: pointer;
	}
	.arrow {
		width: 30px;
		margin: 0 10px;
	}

	.desc-container {
		margin: 36px 0;
	}

	:global(.bounding-box) {
		position: absolute;
		border: 4px solid #d60c0c;
		pointer-events: none;
	}

	.highlight {
		border: 4px solid #d60c0c;
	}

	.alert-icon {
		position: absolute;
		top: 6px;
		right: 6px;
		width: 20px;
		height: 20px;
		z-index: 2;
	}

	.thumbnail-container {
		position: relative;
		display: inline-block;
		margin: 0 2px;
	}

	.alert-icon-main {
		position: absolute;
		top: -7px; /* shift up to overlap border */
		right: -7px; /* shift right to overlap border */
		width: 18px;
		height: 18px;
		z-index: 2;
		background: white;
		border-radius: 50%;
	}

	:global(.alert-icon-thumb) {
		position: absolute;
		top: -6px;
		right: -6px;
		width: 16px;
		height: 16px;
		z-index: 2;
		background: white;
		border-radius: 50%;
	}
</style>
